<template>
    <div class="flex h-screen w-screen overflow-hidden">
        <div ref="sidebarContainerRef" class="shrink-0">
            <SidebarMenu />
        </div>
        <div class="flex min-w-0 flex-1 flex-col">
            <AutomaticProcessingNotification />
            <div
                class="sidebar-content-offset flex flex-1 overflow-hidden transition-[padding-top] duration-150"
            >
                <slot />
            </div>
        </div>
        <ResetButton />
    </div>
</template>

<script setup lang="ts">
    import {
        AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY,
        useAutomaticProcessingPaused,
    } from '@/api/admin';
    import { applyOrderEditedNotificationToCache } from '@/api/order';
    import { applySpecialCaseNotificationToCache } from '@/api/specialCase';
    import AutomaticProcessingNotification from '@/components/common/AutomaticProcessingNotification.vue';
    import { useSignalRNotifications } from '@/composables/useSignalRNotification';
    import {
        QUERY_LOCATION_INFO,
        QUERY_ORDER,
        QUERY_ORDERS,
        QUERY_PROCESSED_ORDERS,
        QUERY_SPECIAL_CASE_AUTOCOMPLETE,
        QUERY_SPECIAL_CASE_LIST,
        QUERY_SPECIAL_CASE_PARTNERS,
    } from '@/constants';
    import type { AutomaticProcessingStatusNotification } from '@/models/automaticProcessingStatus';
    import useSignalRStore from '@/stores/useSignalRStore';
    import { invalidateQueryKeys } from '@/utils/queryInvalidation';
    import { useQueryClient } from '@tanstack/vue-query';
    import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

    const props = withDefaults(defineProps<{ manageOrdersToProcess?: boolean }>(), {
        manageOrdersToProcess: true,
    });

    useAutomaticProcessingPaused();
    const signalRStore = useSignalRStore();
    const queryClient = useQueryClient();

    useSignalRNotifications({
        notificationType: ['CreditNoteProcessAutomatedStatusNotification'],
        onNotificationReceived: notification => {
            signalRStore.updateAutomaticProcessingStatus(
                notification as AutomaticProcessingStatusNotification
            );
        },
    });

    useSignalRNotifications({
        notificationType: [
            'OrderIsEditedNotification',
            'SpecialCaseAddedNotification',
            'SpecialCaseRemovedNotification',
        ],
        onReconnected: () => {
            void invalidateQueryKeys(queryClient, [
                [QUERY_ORDER],
                [QUERY_ORDERS],
                [QUERY_PROCESSED_ORDERS],
                [QUERY_SPECIAL_CASE_LIST],
                [QUERY_SPECIAL_CASE_PARTNERS],
                [QUERY_SPECIAL_CASE_AUTOCOMPLETE],
                [QUERY_LOCATION_INFO],
                AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY,
            ]);
        },
        onNotificationReceived: notification => {
            applyOrderEditedNotificationToCache(queryClient, notification, {
                invalidateProcessedOrders: true,
                removeOrdersToProcess: props.manageOrdersToProcess,
            });
            applySpecialCaseNotificationToCache(queryClient, notification);
        },
    });

    const sidebarContainerRef = ref<HTMLElement | null>(null);
    let sidebarResizeObserver: ResizeObserver | null = null;

    const updateSidebarOffset = () => {
        const width = sidebarContainerRef.value?.getBoundingClientRect().width ?? 0;
        document.documentElement.style.setProperty('--layout-left-offset', `${Math.ceil(width)}px`);
    };

    onMounted(async () => {
        await nextTick();
        updateSidebarOffset();

        sidebarResizeObserver = new ResizeObserver(() => {
            updateSidebarOffset();
        });

        if (sidebarContainerRef.value) {
            sidebarResizeObserver.observe(sidebarContainerRef.value);
        }
    });

    onBeforeUnmount(() => {
        sidebarResizeObserver?.disconnect();
        sidebarResizeObserver = null;
        document.documentElement.style.setProperty('--layout-left-offset', '0px');
    });
</script>
